/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { isError } from "@epic-mw/error-types";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "@epic-mw/app-context";
import { useLocale } from "@epic-mw/i18n";

/**
 * Wraps `<blockquote>` content in double-quotes if not already quoted.
 * Accepts both straight (`"`) and curly (`"` / `"`) quotes as existing delimiters.
 *
 * @param html - HTML string potentially containing `<blockquote>` elements.
 * @returns The processed HTML string, or `""` if input is falsy.
 */
export const ensureQuotesInBlockquotes = (html?: string | null): string => {
	return (
		html?.toString().replace(/<blockquote>([\s\S]*?)<\/blockquote>/g, (_match, content: string) => {
			let trimmed = content?.trim() || "";
			if (!(trimmed.startsWith('"') || trimmed.startsWith("\u201C"))) trimmed = `"${trimmed}`;
			if (!(trimmed.endsWith('"') || trimmed.endsWith("\u201D"))) trimmed = `${trimmed}"`;
			return `<blockquote>${trimmed}</blockquote>`;
		}) || ""
	);
};

/**
 * Appends a `lang` query parameter to relative `href` values in an HTML string,
 * unless the locale is already the first path segment or `lang` is already set.
 * Skipped entirely in locale-mode "never" — those sites don't use query-param locale.
 *
 * @param locale - The locale to append (e.g. `"en"`).
 * @param html - HTML string containing `href` attributes to localize.
 * @returns The HTML string with localized hrefs, or `""` if input is falsy.
 */
export const localizeLinks = (locale: string, html?: string | null): string => {
	// In "never" mode, URLs are locale-free — don't append ?lang
	if (process.env.NEXT_PUBLIC_LOCALE_MODE === "never") {
		return html || "";
	}

	return (
		// catch any href values, contained in double or single quote
		html?.replace(/href=(["'])(\/[^"']+)\1/g, (match, quote, href: string) => {
			try {
				const tempBase = "https://base";
				const url = new URL(href, tempBase);

				// don't append lang param if locale path is already present
				const pathSegments = url.pathname.split("/").filter(Boolean);
				if (pathSegments.length > 0 && pathSegments[0] === locale) {
					return match;
				}

				// append lang param if not already present
				if (!url.searchParams.has("lang")) {
					url.searchParams.append("lang", locale);
				}
				const updatedHref = url.href.replace(tempBase, "");
				return `href=${quote}${updatedHref}${quote}`;
			} catch (error) {
				console.error("CosmosRichText - Error localizing relative href: ", href, error);
				return match;
			}
		}) || ""
	);
};

const ytDomainRegex = new RegExp('src="(http:|https:)?(//)?(www.)?youtube.com/embed/', "ig");
const ytNoCookieReplacement = 'src="https://www.youtube-nocookie.com/embed/';

/**
 * Replaces `youtube.com/embed/` src URLs with the privacy-friendly `youtube-nocookie.com` domain.
 *
 * @param copy - HTML string potentially containing YouTube embed iframes.
 * @returns The HTML string with YouTube embed domains replaced.
 */
export const youtubeEmbedSanitize = (copy: string): string => {
	return copy.replace(ytDomainRegex, ytNoCookieReplacement);
};

/**
 * Ensures Vimeo embed `src` URLs include the `dnt=true` query parameter.
 *
 * @param copy - HTML string potentially containing Vimeo embed iframes.
 * @returns The HTML string with `dnt=true` appended to all Vimeo player URLs.
 */
export const vimeoEmbedSanitize = (copy: string): string => {
	let sanitized = copy || "";

	if (sanitized.indexOf("player.vimeo.com") > -1) {
		const vimeoDomainRegex = new RegExp('src="((http:|https:)?//player.vimeo.com/video/[a-z0-9?=&;_]+)"', "ig");

		let match = vimeoDomainRegex.exec(sanitized);
		while (match !== null) {
			if (match && match.length && match[1]) {
				const originalVimeoSrc = match[1].replace(/&amp;/gi, "&");
				try {
					const parsedUrl = new URL(originalVimeoSrc);
					if (parsedUrl && parsedUrl.searchParams.get("dnt") !== "true") {
						parsedUrl.searchParams.set("dnt", "true");
						sanitized = sanitized.replace(match[1], parsedUrl.toString());
						sanitized = sanitized.replace(/&amp%3B/gi, "&amp;");
					}
				} catch (e) {
					console.error("Failed to create URL from vimeosrc", isError(e) && e.message);
				}
			}
			match = vimeoDomainRegex.exec(sanitized);
		}
	}
	return sanitized;
};

/**
 * Replaces non-breaking hyphens (U+2011) with regular hyphens (U+002D).
 * Required because CMS content sometimes contains Unicode 8209 which does not
 * render correctly on all platforms (e.g. macOS).
 *
 * @param text - String potentially containing Unicode non-breaking hyphens.
 * @returns The string with all U+2011 characters replaced by `-`.
 */
export const replaceUnicodeDash = (text: string): string => {
	let finalText = text;
	if (finalText && finalText.indexOf("\u2011") !== -1) {
		// replace char code 8209 with 45
		finalText = finalText.replace(/\u2011/g, "-");
	}
	return finalText;
};

/**
 * Replaces `/:locale` and `/{locale}` path placeholders with the given locale.
 *
 * @param locale - The locale to substitute (e.g. `"en"`).
 * @param copy - HTML string potentially containing locale placeholders.
 * @returns The HTML string with locale placeholders replaced.
 */
export const replaceDynamicLocale = (locale: string, copy: string): string => {
	let finalCopy = copy;
	if (finalCopy && finalCopy.indexOf("/:locale") > -1) {
		finalCopy = finalCopy.replace(/\/:locale/gi, `/${locale}`);
	}
	if (finalCopy && finalCopy.indexOf("/{locale}") > -1) {
		finalCopy = finalCopy.replace(/\/\{locale\}/gi, `/${locale}`);
	}
	return finalCopy;
};

/**
 * Regex replacer callback for {@link sanitizeTextAlign}.
 * Maps `left` → `text-align:start` and `right` → `text-align:end`;
 * all other values pass through unchanged.
 *
 * @param _ - Full regex match (unused).
 * @param dir - The captured alignment direction (e.g. `"left"`, `"right"`).
 * @returns The logical `text-align` CSS declaration.
 */
export const replaceTextAlign = (_: string, dir: string): string => {
	const cleanDir = dir.toLowerCase();
	if (cleanDir === "left") return "text-align:start";
	if (cleanDir === "right") return "text-align:end";
	return `text-align:${dir}`;
};

/**
 * Replaces directional CSS `text-align` values with their logical equivalents to
 * support RTL/LTR layouts correctly.
 *
 * - `text-align: left`  → `text-align:start`
 * - `text-align: right` → `text-align:end`
 *
 * This is necessary because CMS-generated HTML often contains physical alignment
 * values that break mirrored layouts in RTL locales (e.g. Arabic, Hebrew).
 *
 * @param html - HTML string potentially containing inline `text-align` styles.
 * @returns The HTML string with directional text-align values replaced by logical values,
 *          or the original string unchanged if no replacements were needed.
 */
export const sanitizeTextAlign = (html: string): string => {
	if (!html || !html.toLowerCase().includes("text-align:")) return html;

	return html.replace(/text-align:\s*(left|right)/gi, replaceTextAlign);
};

/** Remove empty paragraphs commonly left by CKEditor / CMS content */
const removeEmptyParagraphs = (html: string): string => {
	return html.replace(/<p[^>]*>\s*(&nbsp;|&#160;)?\s*<\/p>/gi, "");
};

export const formatHtml = (locale: string, _location: URL, html?: string | null): string => {
	if (!html) return "";

	html = removeEmptyParagraphs(html);
	html = youtubeEmbedSanitize(html);
	html = vimeoEmbedSanitize(html);
	html = replaceUnicodeDash(html);
	html = replaceDynamicLocale(locale, html);

	html = sanitizeTextAlign(html);
	html = ensureQuotesInBlockquotes(html);

	if (locale) {
		html = localizeLinks(locale, html);
	}

	return html;
};

/**
 * React hook that applies {@link formatHtml} to the given HTML, memoized on
 * locale, location, html, and options changes.
 *
 * @param html - Raw HTML string to format.
 * @param options - Optional pipeline flags (see {@link formatHtml}).
 * @returns Memoized sanitized HTML string.
 */
export const useFormatHtml = (html?: string | null, options?: { gameMode?: boolean }): string => {
	const location = useLocation();
	const locale = useLocale();

	const baseHtml = useMemo(() => {
		return formatHtml(locale, location, html);
	}, [locale, location, html]);

	const [gameHtml, setGameHtml] = useState<string | null>(null);

	useEffect(() => {
		if (!options?.gameMode || !baseHtml) {
			setGameHtml(null);
			return;
		}

		let cancelled = false;
		void import("./CosmosRichText.game").then(({ cleanForGame }) => {
			if (!cancelled) {
				setGameHtml(cleanForGame(location, baseHtml));
			}
		});
		return () => {
			cancelled = true;
		};
	}, [options?.gameMode, baseHtml, location]);

	if (options?.gameMode) {
		return gameHtml ?? "";
	}
	return baseHtml;
};
